import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DIALOGS_TABLE_NAME, type ConversationListItem } from '@/entities/conversations';
import { useHasPermission } from '@/entities/profile';
import { toDataGridFilters, toDataGridSorting, useSettings, useTablePresets, useTableState } from '@/entities/settings';
import { useSmartSearchLazy } from '@/features/smart-search';
import { DialogListDataGrid } from './DialogListDataGrid';
import { DEFAULT_DIALOG_SORTING } from './model/constants';
import { useDescriptionActions, useFiltersDescription } from './model/filtersDescription';
import { useColumnsPanelWidth } from './model/useColumnsPanelWidth';
import { useCommentChangeReload } from './model/useCommentChangeReload';
import { useContentSearchState } from './model/useContentSearchState';
import { useDialogColumns } from './model/useDialogColumns';
import { useDialogDownload } from './model/useDialogDownload';
import { useDialogRowClick } from './model/useDialogRowClick';
import { useDialogSearchHandlers } from './model/useDialogSearchHandlers';
import { useExternalSelection } from './model/useExternalSelection';
import { useFilterLayerActions } from './model/useFilterLayerActions';
import { useLazyPageFetch } from './model/useLazyPageFetch';
import { useListScrollPosition } from './model/useListScrollPosition';
import { useMassActions } from './model/useMassActions';
import { usePresetRoute } from './model/usePresetRoute';

/**
 * Раскрытых групп колонок ещё нет в настройках — все свёрнуты.
 * Ссылка постоянная: панель управляется контролируемо, и новый пустой массив на каждый рендер
 * выглядел бы для неё сменой состояния.
 */
const NO_EXPANDED_GROUPS: string[] = [];

/** Пропсы секции списка диалогов */
export type DialogListSectionProps = {
  onLoaded?: (data: ConversationListItem[]) => void;
  /**
   * Узел вне колонки списка, в который переносится блок управления. Задают раскладки, где
   * рядом со списком стоит карточка диалога и колонке списка блоку тесно. Промежуток от
   * перенесённого блока до списка держит раскладка — по метке плотности на самом блоке.
   */
  controlsContainer?: HTMLElement | null;
  className?: string;
};

/**
 * Секция списка диалогов на базе DataGrid.
 * Оркеструет состояние списка (поиск, колонки, выборка перехода, массовые действия, выгрузка)
 * через хуки в `./model` и передаёт готовые пропсы презентеру `DialogListDataGrid`.
 */
export const DialogListSection: FC<DialogListSectionProps> = ({ className, controlsContainer, onLoaded }) => {
  const { id } = useParams<{ id?: string }>();
  const { dialogsPage: dialogsSettings, setDialogsSettings } = useSettings();

  // Массовый запуск обработки диалогов доступен только под отдельным разрешением.
  const canRunPipeline = useHasPermission('ADMIN_CONVERSATION_PIPELINE');

  const { settings, actions } = useTableState(DIALOGS_TABLE_NAME, 'localStorage', { sorting: DEFAULT_DIALOG_SORTING });
  const {
    activePreset,
    isDirty: isPresetDirty,
    apply: applyPreset,
    enterView,
    exitSelection,
    saveCurrentToActivePreset,
  } = useTablePresets(DIALOGS_TABLE_NAME);

  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useCommentChangeReload(triggerReload);

  // --- внешняя выборка диалогов (переход с авто-инсайта/зоны качества дашборда) ---
  const {
    filters: effectiveSettingsFilters,
    sorting: selectionSorting,
    activeSelectionKey,
    activeSelection,
    unsavedSelection,
    onDismiss: onSelectionHintDismiss,
  } = useExternalSelection({
    tableName: DIALOGS_TABLE_NAME,
    filters: settings.filters ?? [],
    sorting: settings.sorting ?? DEFAULT_DIALOG_SORTING,
    dismissedSelectionKey: settings.dismissedSelectionKey,
    onDismissedSelectionKeyChange: actions.setDismissedSelectionKey,
    selectionDescription: settings.selectionDescription,
    onSelectionDescriptionChange: actions.setSelectionDescription,
  });

  // Настройки с наложенным слоем выборки: на свежем переходе фильтры/сортировка уже отражают выборку
  // и сортировку по умолчанию — их и передаём в состояние поиска по содержимому.
  const effectiveSettings = useMemo(
    () => ({ ...settings, filters: effectiveSettingsFilters, sorting: selectionSorting }),
    [settings, effectiveSettingsFilters, selectionSorting],
  );

  // --- состояние поиска по содержимому диалога ---
  const contentSearch = useContentSearchState({ settings: effectiveSettings, actions, onReload: triggerReload });
  const {
    activeMode: activeContentMode,
    committedValue: committedContentValue,
    hasSearch: hasContentSearch,
    columnsMode: contentColumnsMode,
    reset: resetContentSearch,
    contentSearchOptions,
    effectiveSorting: effectiveSettingsSorting,
    handleSortingChange: handleContentSearchSortingChange,
    filterUserColumnOrder,
    getEffectiveColumnOrder,
  } = contentSearch;

  const sorting = useMemo<DataGridSortingState>(
    () => toDataGridSorting(effectiveSettingsSorting),
    [effectiveSettingsSorting],
  );
  const filters = useMemo<DataGridFilterState>(
    () => toDataGridFilters(effectiveSettingsFilters),
    [effectiveSettingsFilters],
  );

  // --- колонки таблицы и параметры запроса из их видимости ---
  const {
    columns,
    isLoading,
    isReady,
    gridColumnProps,
    columnOrder,
    columnPinning,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    searchDialogsPage,
  } = useDialogColumns({
    settings,
    actions,
    filters,
    sorting,
    contentSearch: { mode: contentColumnsMode, hasQuery: hasContentSearch },
    filterUserColumnOrder,
    getEffectiveColumnOrder,
    mainColumns: dialogsSettings.mainColumns,
    onMainColumnsChange: (mainColumns) => setDialogsSettings({ mainColumns }),
  });

  const {
    value,
    onChange,
    searchMode,
    setSearchMode,
    predicateInfo,
    searchError,
    fetchData,
    buildRequestParams,
    clearSearch,
    isSearchLoading,
  } = useSmartSearchLazy({
    entityType: 'conversation',
    initialSearch: settings.search,
    dialogsPage: searchDialogsPage,
    contentSearchOptions,
  });

  const { totalCount, cacheVersion, gridKey, fetchPage } = useLazyPageFetch({
    fetchData,
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    onLoaded,
    cacheKey: {
      gridSorting: sorting,
      gridFilters: filters,
      visibleMainColumns,
      visibleAttributePaths,
      expertReportSearchParams,
      activeContentMode,
      committedContentValue,
      reloadKey,
    },
  });

  const { listCompositionKey, initialScrollIndex, onScrollIndexChange } = useListScrollPosition({
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    search: settings.search,
    activeContentMode,
    committedContentValue,
    savedScrollKey: settings.scrollKey,
    savedScrollIndex: settings.scrollIndex,
    onScrollIndexChange: actions.setScrollIndex,
  });

  const {
    isMassActionsActive,
    rowSelection,
    selectedCount,
    hasPipelineSelection,
    isPreparingSelection,
    isRunPipelinePending,
    onToggleMassActions,
    onRowSelectionChange,
    onRunPipeline,
  } = useMassActions({
    canRunPipeline,
    fetchData,
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    listCompositionKey,
  });

  const { onDownloadList, isDownloadPending } = useDialogDownload({
    buildRequestParams,
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    columnOrder,
    columnPinning,
  });

  const { onSortingChange, onFiltersChange, onSearch, onClearSearch, onModeChange } = useDialogSearchHandlers({
    searchMode,
    searchValue: value,
    setSearchMode,
    clearSearch,
    onContentSearchSortingChange: handleContentSearchSortingChange,
    applyFilters: actions.onFiltersChange,
    commitSearch: actions.setSearch,
    triggerReload,
  });

  const { canReset, canSaveActivePreset, canDeselectSelection, onResetFilters, onDeselectSelection } =
    useFilterLayerActions({
      activePreset,
      isPresetDirty,
      applyPreset,
      enterView,
      exitSelection,
      filters: effectiveSettingsFilters,
      sorting: settings.sorting,
      activeSelectionKey,
      onFiltersChange: actions.onFiltersChange,
      onClear: onClearSearch,
      onResetContentSearch: resetContentSearch,
    });

  const onClickRow = useDialogRowClick();

  const filtersDescription = useFiltersDescription({
    columns,
    columnOrder,
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    selection: activeSelection,
  });

  // Описание не только читается, но и управляет отбором: пишет то же состояние таблицы,
  // что и контролы в шапке колонки.
  const filtersDescriptionActions = useDescriptionActions({
    sorting: effectiveSettingsSorting,
    onSortingChange: handleContentSearchSortingChange,
    filters: effectiveSettingsFilters,
    onFiltersChange: actions.onFiltersChange,
    canRemoveSelection: canDeselectSelection,
    onRemoveSelection: onDeselectSelection,
  });

  // Адрес раздела закреплённого пресета: открытый адрес применяет пресет, а смена пресета
  // в списке переводит на его адрес (или на обычный список диалогов).
  const { onActivePresetChange } = usePresetRoute(DIALOGS_TABLE_NAME);

  // Ширина панели управления колонками: локальное состояние на время перетаскивания ручки,
  // запись в настройки таблицы — с задержкой.
  const { width: columnsPanelWidth, onWidthChange: onColumnsPanelWidthChange } = useColumnsPanelWidth(
    settings.columnsPanelWidth,
    actions.setColumnsPanelWidth,
  );

  return (
    <DialogListDataGrid
      className={className}
      controlsContainer={controlsContainer}
      columns={columns}
      sorting={sorting}
      onSortingChange={onSortingChange}
      filters={filters}
      onFiltersChange={onFiltersChange}
      totalCount={totalCount}
      fetchPage={fetchPage}
      cacheVersion={cacheVersion}
      gridKey={gridKey}
      searchValue={value}
      onSearchChange={onChange}
      onSearch={onSearch}
      searchMode={searchMode}
      onModeChange={onModeChange}
      onClearSearch={onClearSearch}
      predicateInfo={predicateInfo}
      searchError={searchError}
      isSearchLoading={isSearchLoading}
      isLoading={isLoading || !isReady}
      activeRowId={id}
      onClickRow={onClickRow}
      initialScrollIndex={initialScrollIndex}
      onScrollIndexChange={onScrollIndexChange}
      gridColumnProps={gridColumnProps}
      columnsPanelWidth={columnsPanelWidth}
      onColumnsPanelWidthChange={onColumnsPanelWidthChange}
      columnsPanelExpandedGroups={settings.columnsPanelExpandedGroups ?? NO_EXPANDED_GROUPS}
      onColumnsPanelExpandedGroupsChange={actions.setColumnsPanelExpandedGroups}
      activeSidePanel={settings.activeSidePanel ?? null}
      onActiveSidePanelChange={actions.setActiveSidePanel}
      isSidePanelPinned={Boolean(settings.sidePanelPinned)}
      onSidePanelPinnedChange={actions.setSidePanelPinned}
      filtersDescription={filtersDescription}
      filtersDescriptionActions={filtersDescriptionActions}
      activeContentTab={contentSearch.activeTab}
      contentSearchInputValue={contentSearch.inputValue}
      onContentSearchInputChange={contentSearch.setInputValue}
      onContentSearchClear={contentSearch.clearInput}
      onContentSearchTabChange={contentSearch.handleTabChange}
      onContentSearchSubmit={contentSearch.handleSubmit}
      phraseSearchRole={contentSearch.phraseSearchRole}
      onPhraseSearchRoleChange={contentSearch.handlePhraseSearchRoleChange}
      phraseSearchEmployeeIds={contentSearch.phraseSearchEmployeeIds}
      onPhraseSearchEmployeeIdsChange={contentSearch.handlePhraseSearchEmployeeIdsChange}
      commentAuthorIds={contentSearch.commentAuthorIds}
      onCommentAuthorIdsChange={contentSearch.handleCommentAuthorIdsChange}
      tableName={DIALOGS_TABLE_NAME}
      unsavedSelection={unsavedSelection}
      onSelectionHintDismiss={onSelectionHintDismiss}
      canReset={canReset}
      onResetFilters={onResetFilters}
      canSaveActivePreset={canSaveActivePreset}
      activePresetName={activePreset?.name}
      onSaveActivePreset={saveCurrentToActivePreset}
      canDeselectSelection={canDeselectSelection}
      onDeselectSelection={onDeselectSelection}
      onDownloadList={onDownloadList}
      isDownloadPending={isDownloadPending}
      onActivePresetChange={onActivePresetChange}
      canRunPipeline={canRunPipeline}
      isMassActionsActive={isMassActionsActive}
      onToggleMassActions={onToggleMassActions}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      selectedCount={selectedCount}
      hasPipelineSelection={hasPipelineSelection}
      onRunPipeline={onRunPipeline}
      isRunPipelinePending={isRunPipelinePending || isPreparingSelection}
      isPreparingSelection={isPreparingSelection}
    />
  );
};
